//functions 
import { connect } from 'react-redux';
import * as ComponentFunctions from './Settlement.Form.Functions';
import { useEffect, useState } from 'react';
import { DefaultButton, Panel, PrimaryButton, Spinner, MessageBarType } from '@fluentui/react';
import { HeaderPersonaComponent } from '../../../commons/component/headers/HeaderPersona/HeaderPersonaComponent';
import { Field, Input, InputOnChangeData } from '@fluentui/react-components';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import SettlementDetailsByConceptComponent from '../../Settlements/SettlementDetails/SettlementDetailsByConcept.Component';

////i18Next
import { useTranslation, Trans } from 'react-i18next';

//styles
import './Settlement.Form.Styles.css';
import { NewAgreementSettlement, NewPractitionerSettlement, NewProtocolSettlement } from '../../../models/Entities/Settlements/NewSettlement';


interface FormValidation {
    success: boolean;
    errors: string[];
}


const SettlementFormComponent = (props: ComponentFunctions.Props) => {

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>(undefined);

    // form properties
    let defaultSettlementName = '';
    if (props.MedicalInsurance) {
        defaultSettlementName = props.MedicalInsurance.businessName;
    } else if (props.Practitioner) {
        defaultSettlementName = props.Practitioner.fullName;
    } else if (props.Protocol) {
        defaultSettlementName = props.Protocol.title;
    }
    const [settlementName, setSettlementName] = useState<string | undefined>(ComponentFunctions.SuggestSettlementTitle(defaultSettlementName));
    const [settlementDescription, setSettlementDescription] = useState<string>();
    const [settlementStartDate, setSettlementStartDate] = useState<Date>();
    const [settlementEndDate, setSettlementEndDate] = useState<Date>();

    // Remove commented code: //useTranslation();
    const [t] = useTranslation();

    //efects 
    useEffect(() => {
        if (settlementName === undefined) {
            let entity = '';
            if (props.MedicalInsurance) {
                entity = props.MedicalInsurance.businessName;
            } else if (props.Practitioner) {
                entity = props.Practitioner.fullName;
            } else if (props.Protocol) {
                entity = props.Protocol.title;
            }
            setSettlementName(ComponentFunctions.SuggestSettlementTitle(entity));
        }
    }, [settlementName]);

    useEffect(() => {
        if (props.ContractId) {
            props.GetContractById(props.ContractId);
        }
    }, [props.Contract?.id === undefined]);

    useEffect(() => {
        if (props.isSuccessfullyAdded) {
            ResetForm();
            props.dismissPanel();
        }
    }, [props.isSuccessfullyAdded]);

    // After successful practitioner settlement creation, show SettlementDetailsByConcept
    useEffect(() => {
        if (props.isSuccessfullyAdded && props.PractitionerId) {
            // Show the new details component for practitioner settlements
            // You may want to use a modal, panel, or route navigation here
            // For demonstration, render below the form
        }
    }, [props.isSuccessfullyAdded, props.PractitionerId]);

    // event handlers
    const onChangeSettlementName = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined) => {

        setSettlementName(newValue);
    }

    const onChangeSettlementDescription = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined) => {

        setSettlementDescription(newValue);
    }

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            // Prepare safe values for required fields
            const safeName = settlementName ?? '';
            const safeDescription = settlementDescription ?? '';
            const safeStartDate = settlementStartDate ?? new Date();
            const safeEndDate = settlementEndDate ?? new Date();

            if (props.Protocol ?? false) {
                const protocolSettlement: NewProtocolSettlement = {
                    name: safeName,
                    contractId: props.ContractId,
                    description: safeDescription,
                    settlementType: 0,
                    settlementDateRangeInit: safeStartDate,
                    settlementDateRangeEnd: safeEndDate,
                    protocolId: props.Protocol!.id,
                    isManualSettlement: false,
                    isIncludedAdditionalConcepts: true,
                    isIncludedRefundableConcepts: true,
                    isIncludeContractConcepts: true
                }
                props.AddProtocolSettlement(protocolSettlement);
            }

            if (props.Practitioner ?? false) {
                const practitionerSettlement: NewPractitionerSettlement = {
                    name: safeName,
                    contractId: props.ContractId,
                    description: safeDescription,
                    settlementType: 0,
                    settlementDateRangeInit: safeStartDate,
                    settlementDateRangeEnd: safeEndDate,
                    practitionerId: props.Practitioner!.userId,
                    isManualSettlement: false
                }
                props.AddPractitionerSettlement(practitionerSettlement);
            }

            if (props.MedicalInsuranceId ?? false) {
                const medicalInsuranceSettlement: NewAgreementSettlement = {
                    name: safeName,
                    description: safeDescription,
                    settlementType: 0,
                    settlementDateRangeInit: safeStartDate,
                    settlementDateRangeEnd: safeEndDate,
                    medicalAgreementId: 0,
                    medicalInsuranceId: 0,
                    isManualSettlement: false
                }
                props.AddAgreementSettlement(medicalInsuranceSettlement);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    // form validation
    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if ((settlementName === undefined || settlementName === '')) {
            validationResult = false;
            FormValidation.errors.push(t('settlementForm.formValidation.requiredName'));
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    const ResetForm = () => {
        setSubmittedForm(false);
        setSettlementName(undefined);
        setSettlementDescription(undefined);
        setSettlementStartDate(undefined);
        setSettlementEndDate(undefined);
    }

    // form content

    const PractitionerInfo = (
        <>
            {props.Practitioner && <HeaderPersonaComponent PersonaName={props.Practitioner?.fullName} secondaryText={props.Practitioner?.speciality} />}
        </>
    )

    const MedicalInsuranceInfo = (
        <>
            <h3>{props.MedicalInsurance?.businessName}</h3>
            <h3>{props.MedicalAgreement?.healthProviderId}</h3>
        </>
    );

    const NoAssociations = (
        <p className="NoData">{t('settlementForm.medicalInsurance.noContractAssociations')}</p>
    )

    // footer
    const onRenderFooterContent = () => {
        return (
            <div className={ComponentFunctions.contentStyles.footer}>
                <DefaultButton onClick={HandleCancelForm} className="OtherOption">
                    <Trans i18nKey={'settlementForm.footer.cancelButton'} />
                </DefaultButton>
                {(props.Practitioner || props.Protocol || props.MedicalInsurance) && <PrimaryButton
                    onClick={HandleSaveForm}
                    styles={ComponentFunctions.buttonStyles}
                    className="ConfirmAction"
                    disabled={props.isAdding}
                >
                    {props.isAdding && <Spinner size={0} />} {/* SpinnerSize.xSmall = 0 */}
                    <Trans i18nKey={'settlementForm.footer.saveButton'} />
                </PrimaryButton>}
            </div>
        );
    };

    return (
        <>
            <Panel
                isOpen={props.isOpen}
                onDismiss={props.dismissPanel}
                headerText={t('settlementForm.panel.newSettlement')}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={onRenderFooterContent}
                isFooterAtBottom={true}
            >
                {props.failOnAdding && <MessageComponent
                    message='Atención:'
                    type={MessageBarType.error}
                    subMessage={props.error?.ErrorMessage}
                    itemList={props.error?.Errors}
                />}
                {(props.PractitionerId || props.Contract?.practitioners[0]?.practitionerId) && PractitionerInfo}
                {(props.MedicalInsuranceId || props.MedicalInsurance) && MedicalInsuranceInfo}
                {submittedForm && !FormValidationStatus?.success && (
                    <MessageComponent
                        type={MessageBarType.error} message={'Verifique la información suministrada.'}
                        itemList={FormValidationStatus?.errors}
                    />
                )}
                {(props.Practitioner || props.Protocol || props.MedicalInsurance) && (
                    <>
                        <Field label={t('settlementForm.formData.settlementName')} required validationState={submittedForm && (!settlementName || settlementName === '') ? 'error' : 'none'} validationMessage={submittedForm && (!settlementName || settlementName === '') ? t('settlementForm.formData.requiredField') : undefined}>
                            <Input
                                name="settlement-name"
                                type="text"
                                autoComplete="off"
                                onChange={(event, data: InputOnChangeData) => onChangeSettlementName(event, data.value)}
                                value={settlementName}
                            />
                        </Field>
                        <Field label={t('settlementForm.formData.settlementDescription')}>
                            <Input
                                name="settlement-description"
                                type="text"
                                autoComplete="off"
                                onChange={(event, data: InputOnChangeData) => onChangeSettlementDescription(event, data.value)}
                                value={settlementDescription}
                            />
                        </Field>
                    </>
                )}
                {!(!props.Contract || !props.MedicalInsuranceId) && !(!props.Practitioner && !props.Protocol && !props.MedicalInsurance) && NoAssociations}
            </Panel>
            {/* Show details by concept if practitioner settlement was just created */}
            {props.isSuccessfullyAdded && props.PractitionerId && props.Contract?.id && (
                <SettlementDetailsByConceptComponent settlementId={props.Contract.id} getSettlementById={props.GetSettlementById} />
            )}
        </>
    )
}

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(SettlementFormComponent as any);